// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngLoadingOverlay from "@ces/App/Components/Common/XngLoadingOverlay";
import TitleTypography, { TitleTypographyStyles } from "@ces/App/Components/TitleTypography/TitleTypography";
import WishListCard, { WishListCardStyles } from "@ces/Overrides/Components/WishListCard";
import { GetWishListsApiParameter } from "@insite/client-framework/Services/WishListService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import updateWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/UpdateWishList";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyProps } from "@insite/mobius/Typography";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
    };
}

// XNG-Changes
interface State {
    lastWishlistsDataView: { value?: WishListModel[]; isLoading: boolean };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    // XNG-Changes
    const recentWishlistsDataView = getWishListsDataView(state, recentWishListsParameter);

    return {
        recentWishListsDataView: recentWishlistsDataView,
        myListsPageNavLink: getPageLinkByPageType(state, "MyListsPage"),
        canViewWishLists: (session.isAuthenticated || session.rememberMe) && !session.isGuest,
        // XNG-Changes
        isLoading: recentWishlistsDataView.isLoading,
    };
};

let recentWishListsParameter = {
    page: 1,
    sort: "ModifiedOn DESC",
    expand: ["top3products", "favorite", "hidden", "tags"],
    wishListLinesSort: "mostRecent",
} as GetWishListsApiParameter;

const mapDispatchToProps = {
    loadWishLists,
    addWishListToCart,
    updateWishList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentWishListsStyles {
    wishListCardGridItem?: GridItemProps;
    noWishListsText?: TypographyProps;
    wishListCard?: WishListCardStyles;
    // XNG-Changes
    cardListStyles?: CardListStyles;
    titleTypographyStyles?: TitleTypographyStyles;
    cardContainer?: CardContainerStyles;
    noWishListsGridItem?: GridItemProps;
    dividerGridItem?: GridItemProps;
}

export const recentWishListsStyles: RecentWishListsStyles = {
    wishListCardGridItem: {
        width: 12,
        css: css`
            padding: 0;
        `,
    },
    wishListCard: {
        actionAddToCartButton: {
            typographyProps: {
                size: 14,
            },
        },
    },
};

const styles = recentWishListsStyles;

class RecentWishLists extends React.Component<Props, State> {
    // XNG-Changes
    constructor(props: Props) {
        super(props);
        this.state = {
            lastWishlistsDataView: {
                value: this.props.recentWishListsDataView.value || [],
                isLoading: this.props.recentWishListsDataView.isLoading,
            },
        };
    }

    componentDidMount() {
        recentWishListsParameter.pageSize = this.props.fields.numberOfRecords;
        if (
            this.props.canViewWishLists &&
            !this.props.recentWishListsDataView.value &&
            !this.props.recentWishListsDataView.isLoading
        ) {
            this.props.loadWishLists(recentWishListsParameter);
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (
            this.props.canViewWishLists &&
            !this.props.recentWishListsDataView.value &&
            !this.props.recentWishListsDataView.isLoading
        ) {
            this.props.loadWishLists(recentWishListsParameter);
        }

        // XNG-Changes
        if (
            this.props.recentWishListsDataView.value !== prevProps.recentWishListsDataView.value &&
            this.props.recentWishListsDataView.value
        ) {
            this.setState({
                lastWishlistsDataView: { ...this.props.recentWishListsDataView },
            });
        }
    }

    toggleFavoriteClickHandler = (wishList: WishListModel) => {
        if (!wishList) {
            return;
        }
        // XNG-Changes
        this.setState({ lastWishlistsDataView: { ...this.props.recentWishListsDataView } });
        this.props.updateWishList({
            apiParameter: {
                wishList: {
                    ...wishList,
                    isFavorite: !wishList.isFavorite,
                    wishListLineCollection: null,
                },
            },
            onComplete(resultProps) {
                if (resultProps.result?.wishList) {
                    this.onSuccess?.();
                } else if (resultProps.result?.errorMessage) {
                    this.onError?.(resultProps.result.errorMessage);
                }
            },
        });
    };
    toggleHideClickHandler = (wishList: WishListModel) => {
        if (!wishList) {
            return;
        }
        // XNG-Changes
        this.setState({ lastWishlistsDataView: { ...this.props.recentWishListsDataView } });
        this.props.updateWishList({
            apiParameter: {
                wishList: {
                    ...wishList,
                    isHidden: !wishList.isHidden,
                    wishListLineCollection: null,
                },
            },
            onComplete(resultProps) {
                if (resultProps.result?.wishList) {
                    this.onSuccess?.();
                } else if (resultProps.result?.errorMessage) {
                    this.onError?.(resultProps.result.errorMessage);
                }
            },
        });
    };

    render() {
        // XNG-Changes : don't show widgte if there are no recent wishlists
        if (!this.props.canViewWishLists || this.props.recentWishListsDataView.value?.length === 0) {
            return null;
        }

        const {
            recentWishListsDataView: { value: recentWishLists },
            myListsPageNavLink,
        } = this.props;
        const myListsPageUrl = myListsPageNavLink ? myListsPageNavLink.url : undefined;

        // XNG-Changes
        const { lastWishlistsDataView } = this.state;

        // XNG-Changes
        return (
            // XNG-Changes: use loading overlay
            <XngLoadingOverlay id="recent-wishlists-widget" loading={this.props.isLoading}>
                <CardList data-test-selector="cardListRecentWishLists" extendedStyles={styles.cardListStyles}>
                    <TitleTypography
                        linkText={translate("View All Saved Lists")}
                        link={myListsPageUrl}
                        extendedStyles={styles.titleTypographyStyles}
                        showIcon={true}
                    >
                        {translate("My Lists")}
                    </TitleTypography>
                    {/* {recentWishLists && recentWishLists.length === 0 && (
                    <GridItem {...styles.noWishListsGridItem}>
                        <Typography {...styles.noWishListsText}>{siteMessage("WishLists_NoWishlistsFound")}</Typography>
                    </GridItem>
                )} */}
                    <GridItem {...styles.dividerGridItem} />
                    {/* XNG-Changes: Changing the structure, adding divider and use lastWishlistsDataView */}
                    {lastWishlistsDataView.value &&
                        lastWishlistsDataView.value.map((wishList: WishListModel, index) => (
                            <React.Fragment key={wishList.id}>
                                <CardContainer extendedStyles={styles.cardContainer}>
                                    <GridItem {...styles.wishListCardGridItem}>
                                        <WishListCard
                                            wishList={wishList}
                                            addWishListToCart={this.props.addWishListToCart}
                                            extendedStyles={styles.wishListCard}
                                            toggleFavorite={() => this.toggleFavoriteClickHandler(wishList)}
                                            toggleHide={() => this.toggleHideClickHandler(wishList)}
                                        />
                                    </GridItem>
                                </CardContainer>
                                {index !== Number(lastWishlistsDataView.value?.length) - 1 && (
                                    <GridItem {...styles.dividerGridItem} />
                                )}
                            </React.Fragment>
                        ))}
                </CardList>
            </XngLoadingOverlay>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentWishLists),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Lists Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 3,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
